/**
 * Recommendation derivation (ADR-009) — pure logic, no React.
 *
 * Turns the current factor picture (the profile's `assessments` + firing
 * `alerts`) into an ordered list of recommendations following the most recent
 * assessment. Recommendations are NOT stored; only the kaimahi's action on one
 * is (see the status helpers below + whanauRecommendations API).
 *
 * Rules:
 *   - A firing alert becomes an URGENT recommendation and supersedes the generic
 *     risk recommendation for the same subdomain (one item, not two).
 *   - A risk factor (Te Korenga) becomes a PRIORITY recommendation.
 *   - A change factor (Te Aranga Ake / Te Ohonga Ake) becomes an EMERGING one.
 *   - A protective factor (Te Mōhiotanga / Te Māramatanga) is a STRENGTH to
 *     maintain — listed separately, not an action item.
 *   - Not-assessed subdomains produce nothing.
 *
 * Order: alerts (by severity) → risk → change, then framework sort order.
 * Prose (focus lines, band chips) lives in i18n; the Theory of Change outcome
 * string comes from the framework via `outcomeFor`.
 */

import type {
  ProfileAlert,
  RecommendationStatusRow,
  RecommendationStatusValue,
  SubdomainAssessmentRow,
} from "../api/client";

export type RecommendationKind = "alert" | "risk" | "change";

export type Recommendation = {
  /** Stable id: "alert:<alert_id>" or "factor:<subdomain_id>". */
  key: string;
  kind: RecommendationKind;
  /** Alert severity, or null for factor-derived recommendations. */
  severity: ProfileAlert["severity"] | null;
  subdomainId: string;
  domainId: string;
  pouId: string;
  /** Raw English subdomain name — a fallback; the UI prefers frameworkLabel(). */
  subdomainNameEn: string;
  /** Theory of Change outcome, or "" if the framework has none. */
  outcome: string;
  /** Alert description (alerts only). */
  description: string | null;
  /** Current factor state driving this recommendation — sent as state_kind_at_action. */
  stateKind: "risk" | "change";
  recordedAt: string | null;
  sortOrder: number;
};

export type Strength = {
  subdomainId: string;
  domainId: string;
  subdomainNameEn: string;
  outcome: string;
  recordedAt: string | null;
  sortOrder: number;
};

export type DerivedRecommendations = {
  recommendations: Recommendation[];
  strengths: Strength[];
};

const SEVERITY_PRIORITY: Record<ProfileAlert["severity"], number> = {
  urgent: 0,
  warn: 1,
  info: 2,
};

/**
 * Derive recommendations + strengths from the current factor picture.
 *
 * @param outcomeFor resolves a subdomain id to its Theory of Change outcome
 *        (from the merged framework — admin renames/overrides applied).
 */
export function deriveRecommendations(
  assessments: SubdomainAssessmentRow[],
  alerts: ProfileAlert[],
  outcomeFor: (subdomainId: string) => string | undefined,
): DerivedRecommendations {
  const stateBySubdomain = new Map<string, SubdomainAssessmentRow>();
  for (const a of assessments) stateBySubdomain.set(a.subdomain_id, a);

  const alertSubdomains = new Set(alerts.map((al) => al.subdomain_id));

  const recommendations: Recommendation[] = [];

  // 1. Alerts → urgent recommendations.
  for (const al of alerts) {
    const a = stateBySubdomain.get(al.subdomain_id);
    const stateKind: "risk" | "change" =
      a?.state_kind === "change" ? "change" : "risk";
    recommendations.push({
      key: `alert:${al.alert_id}`,
      kind: "alert",
      severity: al.severity,
      subdomainId: al.subdomain_id,
      domainId: a?.domain_id ?? "",
      pouId: a?.pou_id ?? "",
      subdomainNameEn: al.subdomain_name_en,
      outcome: outcomeFor(al.subdomain_id) ?? "",
      description: al.description,
      stateKind,
      recordedAt: a?.recorded_at ?? null,
      sortOrder: a?.sort_order ?? al.sort_order,
    });
  }

  // 2. Risk + change factors → recommendations; protective → strengths.
  const strengths: Strength[] = [];
  for (const a of assessments) {
    if (a.state_kind === "protective") {
      strengths.push({
        subdomainId: a.subdomain_id,
        domainId: a.domain_id,
        subdomainNameEn: a.name_en,
        outcome: outcomeFor(a.subdomain_id) ?? "",
        recordedAt: a.recorded_at,
        sortOrder: a.sort_order,
      });
      continue;
    }
    if (a.state_kind !== "risk" && a.state_kind !== "change") continue;
    // An alert on this subdomain already covers it as urgent — don't duplicate.
    if (alertSubdomains.has(a.subdomain_id)) continue;
    recommendations.push({
      key: `factor:${a.subdomain_id}`,
      kind: a.state_kind,
      severity: null,
      subdomainId: a.subdomain_id,
      domainId: a.domain_id,
      pouId: a.pou_id,
      subdomainNameEn: a.name_en,
      outcome: outcomeFor(a.subdomain_id) ?? "",
      description: null,
      stateKind: a.state_kind,
      recordedAt: a.recorded_at,
      sortOrder: a.sort_order,
    });
  }

  // Order: alerts (by severity) → risk → change, then framework sort order.
  const rank = (r: Recommendation): number =>
    r.kind === "alert"
      ? SEVERITY_PRIORITY[r.severity ?? "info"]
      : r.kind === "risk"
        ? 10
        : 20;
  recommendations.sort(
    (x, y) => rank(x) - rank(y) || x.sortOrder - y.sortOrder,
  );
  strengths.sort((x, y) => x.sortOrder - y.sortOrder);

  return { recommendations, strengths };
}

// ---------------------------------------------------------------------------
// Status overlay — the tracked action per recommendation (from the profile).
// ---------------------------------------------------------------------------

/** Index the profile's status rows by recommendation key. */
export function statusByKey(
  rows: RecommendationStatusRow[] | undefined,
): Map<string, RecommendationStatusRow> {
  const map = new Map<string, RecommendationStatusRow>();
  for (const r of rows ?? []) map.set(r.recommendation_key, r);
  return map;
}

/** Effective status for a recommendation — 'open' when never acted on. */
export function effectiveStatus(
  key: string,
  byKey: Map<string, RecommendationStatusRow>,
): RecommendationStatusValue {
  return byKey.get(key)?.status ?? "open";
}

/** Counts for the tile: open recommendations, and open urgent ones (badge). */
export function summarise(
  recommendations: Recommendation[],
  byKey: Map<string, RecommendationStatusRow>,
): { openCount: number; urgentOpenCount: number } {
  let openCount = 0;
  let urgentOpenCount = 0;
  for (const r of recommendations) {
    if (effectiveStatus(r.key, byKey) !== "open") continue;
    openCount++;
    if (r.kind === "alert" && r.severity === "urgent") urgentOpenCount++;
  }
  return { openCount, urgentOpenCount };
}
